import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth } from '../../lib/firebaseAuth';
import {
  disconnectPushDevice, loadPushSDK, pushRequest, pushSupport, serializePush, PushSDK,
} from '../../lib/webPush';

type PushState = {
  uid: string | null; available: boolean; enabled: boolean; subscribed: boolean;
  needsInstall: boolean; supported: boolean; busy: boolean; ready: boolean;
  denied: boolean; error: string;
  engagementAvailable: boolean; engagementEnabled: boolean;
};
const initial: PushState = {
  uid: null, available: false, enabled: false, subscribed: false, needsInstall: false,
  supported: false, busy: false, ready: false, denied: false, error: '',
  engagementAvailable: false, engagementEnabled: false,
};
const PushContext = createContext({ ...initial, enable: async () => {}, disable: async () => {},
  // eslint-disable-next-line no-unused-vars -- Default context retains the setter's boolean signature.
  setEngagement: async (_enabled: boolean) => {} });
export const usePush = () => useContext(PushContext);

export default function PushProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState(initial);
  const sdkRef = useRef<PushSDK>();
  const generation = useRef(0);
  const actionBusy = useRef(false);

  useEffect(() => {
    let active = true;
    let removeListeners = () => {};
    const unsubscribe = auth.onAuthStateChanged(user => {
      const version = ++generation.current;
      const current = () => active && generation.current === version && auth.currentUser?.uid === user?.uid;
      removeListeners();
      sdkRef.current = undefined;
      setState({ ...initial, uid: user?.uid || null, busy: Boolean(user), ...pushSupport() });
      serializePush(async () => {
        await disconnectPushDevice();
        if (!user || !current()) return;
        const config = await pushRequest();
        if (!current()) return;
        setState(value => ({ ...value, available: config.available, enabled: config.enabled,
          engagementAvailable: config.engagementAvailable === true,
          engagementEnabled: config.engagementEnabled === true }));
        const support = pushSupport();
        if (!config.available || !support.supported || support.needsInstall) return;
        const settings = await pushRequest('prepare');
        if (!current() || !settings.appId || !settings.externalId) return;
        const sdk = await loadPushSDK(settings.appId, settings.safariWebId);
        if (!current()) return;
        // init may restore a previous browser identity before we associate this session.
        await sdk.User.PushSubscription.optOut();
        await sdk.login(settings.externalId);
        if (!current()) { await disconnectPushDevice(); return; }
        if (settings.enabled && sdk.Notifications.permission) await sdk.User.PushSubscription.optIn();
        else await sdk.User.PushSubscription.optOut();
        if (!current()) { await disconnectPushDevice(); return; }
        sdkRef.current = sdk;
        const refresh = () => {
          if (current()) setState(value => ({
            ...value, subscribed: sdk.User.PushSubscription.optedIn === true,
            denied: Notification.permission === 'denied',
          }));
        };
        sdk.User.PushSubscription.addEventListener('change', refresh);
        sdk.Notifications.addEventListener('permissionChange', refresh);
        window.addEventListener('focus', refresh);
        removeListeners = () => {
          sdk.User.PushSubscription.removeEventListener('change', refresh);
          sdk.Notifications.removeEventListener('permissionChange', refresh);
          window.removeEventListener('focus', refresh);
        };
        refresh();
        setState(value => ({ ...value, ready: true }));
      }).catch(error => {
        if (current()) setState(value => ({ ...value, error: error.message || 'Bildirim bağlantısı kurulamadı.' }));
      }).finally(() => {
        if (current()) setState(value => ({ ...value, busy: false }));
      });
    });
    return () => { active = false; generation.current++; unsubscribe(); removeListeners(); };
  }, []);

  const enable = async () => {
    const sdk = sdkRef.current;
    const uid = auth.currentUser?.uid;
    if (!sdk || !uid || actionBusy.current) return;
    const version = generation.current;
    const current = () => generation.current === version && auth.currentUser?.uid === uid;
    actionBusy.current = true;
    setState(value => ({ ...value, busy: true, error: '' }));
    try {
      // Start synchronously inside the click: iOS requires a user gesture.
      await sdk.Notifications.requestPermission();
      await serializePush(async () => {
        if (!current()) return;
        if (!sdk.Notifications.permission) throw new Error('İzin verilmedi. Telefon veya tarayıcı ayarlarından bildirimlere izin verebilirsin.');
        await sdk.User.PushSubscription.optIn();
        if (!current()) { await disconnectPushDevice(); return; }
        const settings = await pushRequest('preference', true);
        if (!settings.available) throw new Error('Bildirim servisi şu anda etkin değil.');
        if (current()) setState(value => ({ ...value, enabled: true, subscribed: sdk.User.PushSubscription.optedIn === true }));
      });
    } catch (error: any) {
      if (current()) {
        await serializePush(() => sdk.User.PushSubscription.optOut()).catch(() => undefined);
        setState(value => ({ ...value, error: error.message || 'Bildirimler açılamadı.' }));
      }
    } finally {
      actionBusy.current = false;
      if (current()) setState(value => ({ ...value, busy: false, denied: Notification.permission === 'denied' }));
    }
  };

  const disable = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || actionBusy.current) return;
    const version = generation.current;
    const current = () => generation.current === version && auth.currentUser?.uid === uid;
    actionBusy.current = true;
    setState(value => ({ ...value, busy: true, error: '' }));
    try {
      await serializePush(async () => {
        if (!current()) return;
        await pushRequest('preference', false);
        if (!current()) return;
        setState(value => ({ ...value, enabled: false, engagementEnabled: false }));
        await sdkRef.current?.User.PushSubscription.optOut();
      });
    } catch (error: any) {
      if (current()) setState(value => ({ ...value, error: error.message || 'Tercih kaydedilemedi.' }));
    } finally {
      actionBusy.current = false;
      if (current()) setState(value => ({ ...value, busy: false }));
    }
  };
  const setEngagement = async (enabled: boolean) => {
    const uid = auth.currentUser?.uid;
    if (!uid || actionBusy.current) return;
    const version = generation.current;
    const current = () => generation.current === version && auth.currentUser?.uid === uid;
    actionBusy.current = true;
    setState(value => ({ ...value, busy: true, error: '' }));
    try {
      await serializePush(async () => {
        if (!current()) return;
        const settings = await pushRequest('engagement', enabled);
        if (current()) setState(value => ({ ...value, engagementEnabled: settings.engagementEnabled === true }));
      });
    } catch (error: any) {
      if (current()) setState(value => ({ ...value, error: error.message || 'Hatırlatma tercihi kaydedilemedi.' }));
    } finally {
      actionBusy.current = false;
      if (current()) setState(value => ({ ...value, busy: false }));
    }
  };
  return <PushContext.Provider value={{ ...state, enable, disable, setEngagement }}>{children}</PushContext.Provider>;
}
